import {
  DEFAULT_COLUMN_SIZE,
  DEFAULT_COLUMN_MIN_SIZE,
  COLUMN_SELECT_ID,
} from "@/constants";
import useSimpleLocalStorage from "@/hooks/useSimpleLocalStorage";
import * as filterFns from "@/lib/filters/filterFunctions";
import {
  createLatestColumnOrder,
  getInitialColumnOrder,
  getInitialColumnVisibility,
  mergeColumnState,
} from "@/lib/table";
import {
  ColumnDef,
  ColumnPinningState,
  ColumnSizingState,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  OnChangeFn,
  Row,
  RowSelectionState,
  TableMeta,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { useCallback, useMemo } from "react";

interface TableProps<TData> {
  data: TData[];
  name: string;
  setRowSelection?: OnChangeFn<RowSelectionState>;
  rowSelection?: RowSelectionState;
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  enableSorting?: boolean;
  enableColumnPinning?: boolean;
  enableRowPinning?: boolean;
  enableFilters?: boolean;
  enableGrouping?: boolean;
  groupBy?: string[];
  meta?: TableMeta<TData>;
  columns: ColumnDef<TData, number>[];
  initialColumnSizes?: Record<string, number>;
  initialColumnVisibility?: Record<string, boolean>;
  getRowId?: (row: TData) => string;
  getRowProps?: (row: { original: TData }) => Record<string, unknown>;
}

export default function useTable<TData>({
  name,
  data,
  setRowSelection,
  rowSelection,
  enableRowSelection,
  enableSorting = false,
  enableColumnPinning = true,
  meta,
  columns,
  initialColumnSizes = {},
  initialColumnVisibility: initialColumnVisibilityProp = {},
  enableRowPinning = false,
  enableFilters = true,
  enableGrouping = false,
  groupBy = [],
  getRowId,
  getRowProps,
}: TableProps<TData>) {
  const initialColumnVisibility = useMemo(
    () => getInitialColumnVisibility(columns, initialColumnVisibilityProp),
    [columns, initialColumnVisibilityProp],
  );

  const initialColumnOrder = useMemo(
    () => getInitialColumnOrder(columns),
    [columns],
  );

  const [columnVisibility, setColumnVisibility] =
    useSimpleLocalStorage<VisibilityState>(
      `table:view:${name}`,
      initialColumnVisibility,
    );

  const [columnPinning, setColumnPinning] =
    useSimpleLocalStorage<ColumnPinningState>(`table:pinning:${name}`, {
      left: rowSelection ? [COLUMN_SELECT_ID] : [],
    });

  const [columnSizing, setColumnSizingInternal] =
    useSimpleLocalStorage<ColumnSizingState>(
      `table:sizing:${name}`,
      initialColumnSizes,
    );

  const setColumnSizing = useCallback(
    (columnSizing: () => ColumnSizingState) => {
      const normalizedColumnSizing = Object.fromEntries(
        Object.entries(columnSizing()).map(([key, value]) => [
          key,
          Math.max(value, DEFAULT_COLUMN_MIN_SIZE),
        ]),
      );
      setColumnSizingInternal((prev) => ({
        ...prev,
        ...normalizedColumnSizing,
      }));
    },
    [setColumnSizingInternal],
  );

  const [columnOrder, setColumnOrder] = useSimpleLocalStorage<string[]>(
    `table:order:${name}`,
    initialColumnOrder,
  );

  const stateColumnVisibility = useMemo(() => {
    return mergeColumnState(initialColumnVisibility, columnVisibility);
  }, [initialColumnVisibility, columnVisibility]);

  const stateColumnSizing = useMemo(() => {
    return mergeColumnState(initialColumnSizes, columnSizing);
  }, [initialColumnSizes, columnSizing]);

  const stateColumnOrder = useMemo(() => {
    return createLatestColumnOrder(initialColumnOrder, columnOrder);
  }, [initialColumnOrder, columnOrder]);

  const stateRowSelection = useMemo(() => {
    return rowSelection !== undefined ? { rowSelection } : {};
  }, [rowSelection]);

  const stateColumnPinning = useMemo(() => {
    if (!enableRowSelection) return columnPinning;
    return ensureSelectColumnFirst(columnPinning, !!enableRowSelection);
  }, [columnPinning, enableRowSelection]);

  const setColumnPinningNormalized: OnChangeFn<ColumnPinningState> =
    useCallback(
      (updater) => {
        setColumnPinning((prev) => {
          const next = typeof updater === "function" ? updater(prev) : updater;
          if (!enableRowSelection) return next;
          return ensureSelectColumnFirst(next, true);
        });
      },
      [enableRowSelection, setColumnPinning],
    );

  const state = useMemo(() => {
    // We need to merge default column state with the one from local storage
    // because if we add a new column, after someone already stored the state in local storage,
    // the new state will not be used.
    // In case of visibility a column that should be hidden by default will be visible.
    return {
      columnVisibility: stateColumnVisibility,
      columnSizing: stateColumnSizing,
      columnOrder: stateColumnOrder,
      columnPinning: stateColumnPinning,
      ...(enableGrouping && {
        grouping: groupBy,
        expanded: true as ExpandedState,
      }),
      ...stateRowSelection,
    };
  }, [
    stateColumnVisibility,
    stateColumnSizing,
    stateColumnOrder,
    stateColumnPinning,
    enableGrouping,
    groupBy,
    stateRowSelection,
  ]);

  const defaultColumn = useMemo(
    () => ({
      size: DEFAULT_COLUMN_SIZE,
      minSize: DEFAULT_COLUMN_MIN_SIZE,
    }),
    [],
  );

  const table = useReactTable({
    data,
    columns,
    filterFns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onRowSelectionChange: setRowSelection,
    onColumnPinningChange: setColumnPinningNormalized,
    manualFiltering: true,
    manualSorting: true,
    enableColumnPinning,
    enableRowPinning,
    enableGrouping,
    // For now it's not possible to expand/collapse rows in the UI, we reuse the grouping functionality flag
    enableExpanding: enableGrouping,
    onColumnSizingChange: setColumnSizing,
    onColumnOrderChange: setColumnOrder,
    state,
    enableFilters,
    enableSorting,
    columnResizeMode: "onChange",
    enableRowSelection,
    defaultColumn,
    meta,
    getRowId,
    groupedColumnMode: "remove",
    ...(getRowProps && { getRowProps: getRowProps }),
  });

  return {
    name,
    table,
    state,
    stateColumnVisibility,
    stateColumnSizing,
    stateColumnOrder,
    stateColumnPinning,
    stateRowSelection,
    initialColumnVisibility,
  };
}

const ensureSelectColumnFirst = (
  pinning: ColumnPinningState,
  forceIncludeSelect: boolean,
): ColumnPinningState => {
  const left = pinning.left ?? [];
  const shouldIncludeSelect =
    forceIncludeSelect || left.includes(COLUMN_SELECT_ID);
  if (!shouldIncludeSelect) return pinning;

  const leftWithoutSelect = left.filter((id) => id !== COLUMN_SELECT_ID);
  return {
    ...pinning,
    left: [COLUMN_SELECT_ID, ...leftWithoutSelect],
  };
};
